<template>
<div class="progress-row" v-tooltip="tooltipContent">
  <div class="progress-top-row">
    <div class="stage-label-left">{{ stageLabel || 'Progress' }}</div>
    <div v-if="showStages && stageNumber && totalStages" class="stage-counter-top">{{ stageNumber }} / {{ totalStages }}</div>
  </div>

  <div class="progress-bar-row">
    <div class="progress-track">
      <div class="progress-fill" :style="{ width: `${percentClamped}%`, backgroundColor: props.strokeColor }" />
    </div>
    <div class="percentage-text">{{ hasPercent ? `${Math.round(percentClamped)}%` : '--' }}</div>
  </div>

  <div class="progress-message">{{ messageText }}</div>

</div>

</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  percent: {
    type: Number,
    default: 0,
  },
  stageLabel: {
    type: String,
    default: null,
  },
  stageNumber: {
    type: Number,
    default: null,
  },
  totalStages: {
    type: Number,
    default: null,
  },
  strokeColor: {
    type: String,
    default: '#2e7d32',
  },
  waitingStrokeColor: {
    type: String,
    default: '#1565C0',
  },
  message: {
    type: String,
    default: '',
  },
  status: {
    type: String,
    default: null,
  },
  showStages: {
    type: Boolean,
    default: true,
  },
  taskUID: {
    type: String,
    default: null,
  },
  userName: {
    type: String,
    default: null,
  },
  parentUID: {
    type: String,
    default: null,
  },
});
const hasPercent = computed(() => props.percent !== null && props.percent !== undefined && props.percent !== '');
const percentClamped = computed(() => {
  const p = Number(props.percent);
  if (Number.isNaN(p)) {
    return 0;
  }
  return Math.max(0, Math.min(100, p));
});
const messageText = computed(() => String(props.message || '').trim() || 'No message');

const tooltipContent = computed(() => {
  const parts = [];
  if (props.taskUID) parts.push(`Task: ${props.taskUID}`);
  if (props.userName) parts.push(`User: ${props.userName}`);
  if (props.parentUID) parts.push(`Parent: ${props.parentUID}`);
  return parts.length > 0 ? parts.join(' | ') : null;
});

</script>

<style lang="scss" scoped>

.progress-row {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 54px;
  box-sizing: border-box;
  gap: 0;
  cursor: pointer;
  padding: 4px 8px 4px 8px;
}

.stage-label-left {
  font-size: 0.68rem;
  font-weight: 600;
  color: #111827;
  text-align: left;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  line-height: 1.2;
}

.progress-top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.stage-counter-top {
  font-size: 0.62rem;
  font-weight: 600;
  text-align: right;
  white-space: nowrap;
}

.progress-bar-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-track {
  flex: 1 1 auto;
  height: 5px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.25s ease;
}

.percentage-text {
  min-width: 36px;
  text-align: right;
  font-size: 0.7rem;
  font-weight: 500;
  color: #111827;
}

.progress-message {
  font-size: 0.62rem;
  color: #1e3a8a;
  font-style: italic;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
